import { Alert, Button, List, Paper, Stack } from "@mui/material";
import { HidingError } from "components/errors";
import React, { useCallback, useEffect, useState } from "react";
import ProfileDetails from "./ProfileDetails";
import styles from './ProfilePage.module.css';
import ProfileItem from "./ProfileItem";
import { useDispatch, useSelector } from "react-redux";
import { setSelected, resetProfile, setPagination } from "store/profile/profileSlice";
import { useAddProfileMutation, useDeleteProfileMutation, useGetProfileAppsQuery, useGetProfileRangesQuery, useGetProfilesQuery, useUpdateProfileMutation } from "services/data/profileApi";
import { useGetAppsQuery } from "services/data/appApi";
import { getDefaultQuerySearch, getManagerSearch } from "store/searchbar/selectors";
import { useQueryStale } from "rtkq/useQueryStale";
import { resetManagerSearch } from "store/searchbar/searchbarSlice";
import { markEndpointStale } from "rtkq/staleManager";


const DEFAULT_ERROR = "Ошибка соединения с сервером";

const ProfilePage = ({setActiveToolbar, defaultSearch}) => {
  
  useEffect( () => setActiveToolbar("profile"), []);

  const dispatch = useDispatch();
  const { selected } = useSelector(state => state.profile);

  const queryArgs = useSelector((state) => getManagerSearch(state.searchbar, 'profile', defaultSearch.profile));
  const appsQueryArgs = getDefaultQuerySearch(defaultSearch.app); 
  
  const profileQuery = useGetProfilesQuery(queryArgs);
  const { data, isError, isFetching } = profileQuery;
  useQueryStale({query: profileQuery, endpointName: 'getProfiles', queryArgs: queryArgs});
  
  const appQuery = useGetAppsQuery(appsQueryArgs);
  const { data: appsData, isError: isGetAppsError } = appQuery;
  useQueryStale({query: appQuery, endpointName: 'getApps', queryArgs: appsQueryArgs});

  const { data: profileApps, isError: isGetProfileAppsError, error: getProfileAppsError} = useGetProfileAppsQuery({profileName: selected?.id}, {skip: !selected});
  const { data: profileRanges, isError: isGetProfileRangesError, error: getProfileRangesError} = useGetProfileRangesQuery({profileName: selected?.id}, {skip: !selected});  
  const { data: modesData, isError: isModesError, error: modesError } = useGetProfilesQuery({mode: 'appModes'});
  
  const [updateProfile, {error: updateProfileError, reset: updateProfileReset}] = useUpdateProfileMutation();
  const [addProfile, {error: addProfileError, reset: addProfileReset}] = useAddProfileMutation();
  const [deleteProfile, {error: deleteProfileError, reset: deleteProfileReset}] = useDeleteProfileMutation();

  const [defaultAppMode, setDefaultAppMode] = useState('');
  const [startDefaultAppMode, setStartDefaultAppMode] = useState('');
  
  useEffect(() => {
    setDefaultAppMode(modesData?.body?.default ?? '');
    setStartDefaultAppMode(modesData?.body?.default ?? '');
  }, [modesData]);

  useEffect(() => { 
    if(selected) {
      setDefaultAppMode( profileApps?.body?.default ? profileApps?.body?.default : (modesData?.body?.default ?? ''));
      setStartDefaultAppMode( profileApps?.body?.default ? profileApps?.body?.default : (modesData?.body?.default ?? ''));
    }
    else {
      setDefaultAppMode(modesData?.body?.default ?? '');
      setStartDefaultAppMode(modesData?.body?.default ?? '');
    }
  }, [profileApps, selected]);

  const openError = () => {  
    return !!updateProfileError || !!addProfileError || !!deleteProfileError || false;
  };

  const hideError = () => {
    if(updateProfileError) updateProfileReset();
    if(addProfileError) addProfileReset();
    if(deleteProfileError) deleteProfileReset();
  };

  const makeProfileItems = () => {
    return selected ? profileRanges?.body?.items : [];
  };

  const makeAppsItems = () => { 
    const items = appsData?.body?.items.map(it => {
      const {package: appPackage, ...data} = it;
      return {...data, id: appPackage};
    });
    
    //если профиль не выбран, то возвращаем все приложения с дефолтными значениями полей
    if(!selected?.id && items) { 
      return items.map(item => {
        const mode = item.profileOnly ? (modesData?.body?.defaultOnlyProfile ?? '') : (modesData?.body?.start ?? '');
        return {...item, mode: mode, version: (modesData?.body?.defaultVersion ?? '')};
      }); 
    }

    return items?.map(item => {
      const app = profileApps?.body?.items.find(el => el.package === item.id);
      const mode = item.profileOnly ? (modesData?.body?.defaultOnlyProfile ?? '') : (modesData?.body?.start ?? '');
      if(app) return {...item, mode: mode, ...app};
      return ({...item, mode: mode, version: (modesData?.body?.defaultVersion ?? '')});
    });    
  };
  
  const getErrorMessage = () => { 
    if(updateProfileError) return updateProfileError?.data?.message ?? DEFAULT_ERROR;
    if(addProfileError) return addProfileError?.data?.message ?? DEFAULT_ERROR;
    if(deleteProfileError) return deleteProfileError?.data?.message ?? DEFAULT_ERROR;
    else return'Ошибка';
  };

  const isItemSelected = useCallback((item) => item?.id===selected?.id, [selected]);
    
  const onItemSelect = useCallback((item) => { 
    isItemSelected(item) ? dispatch(resetProfile()) : dispatch(setSelected(item));
  }, [isItemSelected]);


  const onItemEdit = useCallback((params) => {
    return updateProfile({...params, currentQueryArgs: queryArgs}).unwrap();
  }, [queryArgs]);

  const onItemAdd = useCallback((params) => {
    return addProfile({...params, currentQueryArgs: queryArgs}).unwrap();
  }, [queryArgs]);

  //сбросить фильтр, вернуть дефолтную сортировку, пометить все кэши устаревшими
  const resetFitersAndSort = () => {
    const defaultQuerySearch = getDefaultQuerySearch(defaultSearch.profile);
    if(defaultQuerySearch.sort) {
      //сбрасываем фильтр и ставим сортировку по-умолчанию
      dispatch(
        resetManagerSearch( {active: 'profile', sort: defaultQuerySearch.sort[0]} )
      );
    }
    dispatch(
        markEndpointStale( {endpointName: 'getProfiles'} )
    );
  };

  const onItemDelete = (item) => {
    deleteProfile({...item, currentQueryArgs: queryArgs}).unwrap()
      .then(() => { 
        dispatch(resetProfile());
      })
      .catch(e => console.log(e));
  };

  const renderItems = (items) => { 
    if(!items) {
      return <div className={styles.emptyItem}>Профилей пока нет</div>
    }

    return items.map((item, index) => {
      return  <ProfileItem
                  key={item.id}
                  isSelected={isItemSelected(item)}
                  onSelect={onItemSelect}
                  onDelete={onItemDelete}
                  id={item.id}
                  name={item.name}
                  trial={item.trial}
                  disabled={item.disabled}
                  connected={item.connected}
                  licenceRequired={item.licenceRequired}
              />;             
    });
  };
  
  if(isFetching) return <div>Загрузка...</div>;
  if(isError) return <div>Ошибка!</div>; 
  
  return (
    <div className="page">
      <HidingError 
        open={ openError() }
        message={ getErrorMessage() }
        onClose={hideError}
      />
      <div className={styles.overlayWrapper}>
        <Paper elevation={1} sx={{flexShrink: 0, marginBottom: '4px'}}>
            <Stack direction="row" spacing={1} className="toolbar">
                <Alert 
                  severity="info"
                  sx={{width: "100%", justifyContent: "center", alignItems: "center"}}
                >
                  Профили могут использоваться кассой для получения заранее определённого набора приложений
                </Alert>
            </Stack>
        </Paper>
        <div className="page-content">
          <div className="left">
            <div className="pagelist">
              <div className="pagelist-body">
                <List>{renderItems(data.body.items)}</List>
              </div>  
            </div>
          </div>    
          <div className="right">
            <ProfileDetails
              id={selected?.id}
              name={selected?.name}
              disabled={selected?.disabled}
              licenceRequired={selected?.licenceRequired}
              trial={selected?.trial}
              defaultMode={defaultAppMode}
              changeDefaultMode={setDefaultAppMode}
              startDefaultAppMode={startDefaultAppMode}
              editProfile={onItemEdit}
              addProfile={onItemAdd}
              apps={makeAppsItems()}
              ranges={makeProfileItems()}
              resetFitersAndSort={resetFitersAndSort}
            />
          </div>
        </div>
      </div> 
    </div>
  );
};

export default ProfilePage;